import { supabase } from '@/integrations/supabase/client';
import { getProductVariations } from '@/services/benchmark/productNameMapping';
import { startOfWeek, startOfMonth, format, parseISO } from 'date-fns';

/**
 * ----------------------------------------------------------------
 * ChipChip Database Service
 * ----------------------------------------------------------------
 * Fetches sales data directly from the chipchip_sales_data table
 */

// Database table structure for chipchip_sales_data
interface ChipChipSalesRecord {
  id: string;
  product_name: string;
  date: string;
  order_type: string;
  price: number;
  num_order: number;
  quantity: number;
  created_at: string;
  updated_at: string;
  sync_date: string;
}

// Transformed data format for benchmark compatibility
interface ChipChipProductData {
  product_name: string;
  date: string;
  order_type: string;
  price: number;
  num_order: number;
  quantity: number;
}

/**
 * Fetches ChipChip sales data directly from the database
 */
export async function fetchChipChipSalesDataFromDB(
  productName: string,
  startDate: string,
  endDate: string
): Promise<ChipChipProductData[]> {
  try {
    console.log(`[ChipChip DB] Fetching data for product: ${productName}, dates: ${startDate} to ${endDate}`);

    const { data, error } = await supabase
      .from('chipchip_sales_data')
      .select('product_name, date, order_type, price, num_order, quantity')
      .ilike('product_name', `%${productName}%`)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: false });

    if (error) {
      console.error('Database error fetching ChipChip sales data:', error);
      throw new Error(`Database error: ${error.message}`);
    }

    console.log(`[ChipChip DB] Found ${data?.length || 0} records`);
    return data || [];
  } catch (error) {
    console.error('Error fetching ChipChip sales data from database:', error);
    return []; // Return empty array on error to not break benchmark comparison
  }
}

/**
 * Transform ChipChip database data to benchmark PriceData format
 */
export function transformChipChipDatabaseData(chipchipData: ChipChipProductData[]) {
  return chipchipData.map(item => ({
    date: item.date,
    price: item.price,
    location: item.order_type,
    product_name: item.product_name,
    source: 'chipchip-sales-db',
    num_order: item.num_order,
  }));
}

/**
 * Get unique product names from the database (for debugging/exploration)
 */
export async function getUniqueChipChipProducts(): Promise<string[]> {
  try {
    const { data, error } = await supabase
      .from('chipchip_sales_data')
      .select('product_name')
      .order('product_name');

    if (error) {
      console.error('Error fetching unique products:', error);
      return [];
    }

    // Extract unique product names
    const uniqueProducts = [...new Set(data.map(item => item.product_name))];
    return uniqueProducts;
  } catch (error) {
    console.error('Error getting unique ChipChip products:', error);
    return [];
  }
}

/**
 * Normalizes product names for reliable matching
 */
function normalizeProductName(name: string): string {
  return name
    .toLowerCase()
    .replace(/\s+/g, ' ')           // Replace multiple spaces with single space
    .trim()                          // Remove leading/trailing whitespace
    .normalize('NFC');               // Normalize Unicode characters
}

/**
 * Aggregates order data by frequency (weekly/monthly)
 */
function aggregateOrderDataByFrequency(
  orderData: { date: string; orderCount: number }[],
  frequency: 'daily' | 'weekly' | 'monthly'
): { date: string; orderCount: number }[] {
  if (frequency === 'daily') {
    return orderData;
  }

  const groupedOrders = new Map<string, number>();

  orderData.forEach(item => {
    const date = parseISO(item.date);
    const key = frequency === 'weekly'
      ? format(startOfWeek(date, { weekStartsOn: 1 }), 'yyyy-MM-dd') // Monday
      : format(startOfMonth(date), 'yyyy-MM-dd');

    groupedOrders.set(key, (groupedOrders.get(key) || 0) + item.orderCount);
  });

  // Convert to array and sort by date
  return Array.from(groupedOrders.entries())
    .map(([date, orderCount]) => ({ date, orderCount }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

/**
 * Fetch ChipChip order counts independently for chart display
 */
export async function fetchChipChipOrderCounts(
  productName: string,
  startDate: string,
  endDate: string,
  frequency: 'daily' | 'weekly' | 'monthly' = 'daily'
): Promise<{ date: string; orderCount: number }[]> {
  try {
    console.log(`[ChipChip Orders] Fetching order counts for product: ${productName}, dates: ${startDate} to ${endDate}`);

    // Get all product variations to search for
    const productVariations = getProductVariations(productName);
    console.log(`[ChipChip Orders] Searching for ${productVariations.length} variations:`, productVariations);

    // Fetch data filtered by product names in SQL (no limit)
    const { data, error } = await supabase
      .from('chipchip_sales_data')
      .select('date, num_order, product_name')
      .gte('date', startDate)
      .lte('date', endDate)
      .in('product_name', productVariations)  // Filter by exact product names in SQL
      .order('date', { ascending: true });

    if (error) {
      console.error('Database error fetching ChipChip order counts:', error);
      throw new Error(`Database error: ${error.message}`);
    }

    const filteredData = data || [];
    console.log(`[ChipChip Orders] Found ${filteredData.length} matching records`);
    console.log(`[ChipChip Orders] Total orders:`, filteredData.reduce((sum, item) => sum + (item.num_order || 0), 0));

    if (!filteredData || filteredData.length === 0) {
      console.log(`[ChipChip Orders] No records found for any product variation`);
      return [];
    }

    // Group by date and sum order counts across all product name variations
    const ordersByDate = filteredData.reduce((acc, item) => {
      const date = item.date;
      const orderCount = item.num_order;
      
      // Validate and process order count
      if (orderCount != null && !isNaN(orderCount) && orderCount >= 0) {
        acc[date] = (acc[date] || 0) + orderCount;
      } else {
        console.warn(`[ChipChip Orders] Invalid order count for ${date} (${item.product_name}): ${orderCount}`);
      }
      
      return acc;
    }, {} as Record<string, number>);

    // Convert to array format
    const result = Object.entries(ordersByDate).map(([date, orderCount]) => ({
      date,
      orderCount
    }));

    console.log(`[ChipChip Orders] Final result: ${result.length} unique dates with order data`);
    console.log(`[ChipChip Orders] Total orders found: ${result.reduce((sum, item) => sum + item.orderCount, 0)}`);
    result.forEach(item => {
      console.log(`[ChipChip Orders] Date: ${item.date}, Orders: ${item.orderCount}`);
    });

    // Aggregate by frequency if needed
    const aggregatedResult = aggregateOrderDataByFrequency(result, frequency);
    console.log(`[ChipChip Orders] After ${frequency} aggregation: ${aggregatedResult.length} periods`);

    return aggregatedResult;
  } catch (error) {
    console.error('Error fetching ChipChip order counts:', error);
    return [];
  }
}

export const chipchipDatabaseService = {
  fetchChipChipSalesDataFromDB,
  transformChipChipDatabaseData,
  getUniqueChipChipProducts,
  fetchChipChipOrderCounts,
};